import randomUUID from '@/utils/uuid'
import workflowApi from '@/api/workflowApi'
import { EventBus } from '@/event-bus/event-bus.js'
import { safeStructuredClone } from '@/utils/clone.js'
import { workflowApiHelpers } from '@/api/workflowApiHelpers'
import { updateFontSizeDynamically } from '@/utils/update-font.js'
import { DomainModelRole } from '@/constants/cardTypes'

export const diagramHelpers = {
  methods: {
    checkCharacterLimit (ev, maxLength) {
      const selectedText = window.getSelection().toString()
      if (ev.target.isContentEditable) {
        if (ev.target.innerText.length >= maxLength &&
          ev.key !== 'Backspace' &&
          ev.key !== 'ArrowUp' &&
          ev.key !== 'ArrowDown' &&
          ev.key !== 'ArrowLeft' &&
          ev.key !== 'ArrowRight' &&
          !(ev.key === 'a' && ev.metaKey === true) &&
          !selectedText) {
          ev.preventDefault()
        }
      }
    },

    insertTextAtCursor (elem, text, maxLength) {
      const selection = window.getSelection()
      const range = selection.getRangeAt(0)
      const currentText = elem.textContent
      const selectedText = window.getSelection().toString()

      const remainingCharacters = maxLength - currentText.length + selectedText.length

      if (text.length > remainingCharacters) {
        text = text.slice(0, remainingCharacters)
      }

      // Delete the selected text and insert the new text at the cursor position
      range.deleteContents()
      range.insertNode(document.createTextNode(text))
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    },

    onPaste (ev, elem, maxLength, onWidth = false) {
      if (document.activeElement !== elem) {
        // If the element is not focused, do not paste
        return
      }

      ev.preventDefault()
      const pastedText = (ev.clipboardData || window.clipboardData).getData('text/plain')

      this.insertTextAtCursor(elem, pastedText, maxLength)
      updateFontSizeDynamically(elem, onWidth)
    },

    restoreValue (ev, value) {
      if (ev.target.isContentEditable) {
        ev.target.textContent = value
        ev.target._ignoreBlur = true
        ev.target.blur()
      }
    },

    toggleFocus (ev) {
      if (ev.target.isContentEditable) {
        ev.target.blur()
      } else {
        ev.target.focus()
      }
    },

    changeSelectedEvent (eventId) {
      EventBus.$emit('enable-connection-targets', false)
      if (eventId) {
        this.$router.push({
          path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id + '/' + eventId
        }).catch(() => { })

        const focusedElement = document.activeElement

        if (!!focusedElement?.id && focusedElement.id !== eventId) {
          // If some card has active cursor and another card is selected
          focusedElement.blur()
        }
      } else {
        this.$router.push({
          path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id
        }).catch(() => { })
      }
    },

    blurActionForDescription (ev, node) {
      if (ev.target.innerText.length === 0) {
        this.restoreValue(ev, node.name)
      }
      if (ev.target.innerText !== this.svgGraphData[node.id]?.name) {
        this.svgGraph[node.id].name = ev.target.innerText
        if (ev.relatedTarget?.classList?.contains('captures-description')) {
          // another button was pushed that will
          // handle the update of the description
          return
        }
        workflowApi.updateWorkflow({
          svgGraph: this.svgGraph,
          svgLanes: this.svgLanes,
          svgGroups: this.svgGroups,
          workflow: this.workflow,
          updateNote: {
            action: this.svgGraph[node.id].type === 'bpmn:Task' ? 'updated event text to' : 'updated decision text to',
            target: ev.target.innerText
          }
        })
        // }
      }
    },

    blurOnDescription (ev, node) {
      if (this.isBrowserSafari()) {
        setTimeout(() => {
          // wait for possible click event to fire and update the svgGraph
          this.blurActionForDescription(ev, node)
        }, 1000)
      } else {
        this.blurActionForDescription(ev, node)
      }
    },

    blurActionForConditionLabel: function (ev, node, parentId) {
      // const eventButtons = document.getElementById('event-action-buttons')
      // eventButtons.style.removeProperty('display')
      const labelText = (ev.target.innerText || '').trim()
      const index = this.svgGraphData[node.id]?.conditionLabels?.findIndex(label => label.parentId === parentId)
      if (index >= 0) {
        if (labelText !== this.svgGraphData[node.id]?.conditionLabels[index].name) {
          this.svgGraph[node.id].conditionLabels[index].name = labelText
          if (ev.relatedTarget?.classList?.contains('captures-description')) {
            // another button was pushed that will
            // handle the update of the description
            return
          }
          return workflowApi.updateWorkflow({
            svgGraph: this.svgGraph,
            svgLanes: this.svgLanes,
            svgGroups: this.svgGroups,
            workflow: this.workflow,
            updateNote: {
              action: 'updated label to',
              target: labelText
            }
          })
        }
      } else {
        const placeholder = this.svgGraphData[node.id]?.conditionLabel || '[Label]'
        if (labelText === placeholder) {
          return
        }
        if (!this.svgGraph[node.id]?.conditionLabels) {
          this.svgGraph[node.id].conditionLabels = []
        }
        this.svgGraph[node.id].conditionLabels.push({
          parentId,
          name: labelText
        })
        return workflowApi.updateWorkflow({
          svgGraph: this.svgGraph,
          svgLanes: this.svgLanes,
          svgGroups: this.svgGroups,
          workflow: this.workflow,
          updateNote: {
            action: 'updated label to',
            target: labelText
          }
        })
      }
    },

    blurOnConditionLabels (ev, node, parentId) {
      if (this.isBrowserSafari()) {
        // wait for possible click event to fire and update the svgGraph
        setTimeout(() => { this.blurActionForConditionLabel(ev, node, parentId) }, 1000)
      } else {
        this.blurActionForConditionLabel(ev, node, parentId)
      }
    },

    getConnectionLabel (node, parentId) {
      const index = node?.conditionLabels?.findIndex(label => label.parentId === parentId)
      if (index >= 0) {
        // Show a set label as-is; a cleared one renders blank (trim so "\n" → blank).
        return (node.conditionLabels[index].name || '').trim()
      }
      // No label entry - show the placeholder.
      return node.conditionLabel || '[Label]'
    },

    focusOnConditionLabel (ev) {
      const eventButtons = document.getElementById('event-action-buttons')
      if (eventButtons) {
        eventButtons.style.display = 'none'
      }
    },

    addConnection (targetEventId) {
      const sourceEventId = this.$route.params.eventId
      EventBus.$emit('enable-connection-targets', false)
      workflowApi.addConnectionToEvent(
        sourceEventId,
        targetEventId,
        this.svgGraph,
        this.svgLanes,
        this.svgGroups,
        this.workflow
      )
    },

    async addCardToEvent (eventId, cardTypeId, text) {
      const index = this.workflow.domainEvents.findIndex((event) => event.id === eventId)

      // This snippet checks if there is already a card that is an aggregate root or command in the event
      // There should be only one card of each
      if (this.workflow.domainEvents[index].cards) {
        const eventCards = this.workflow.domainEvents[index].cards
        const triggerAggregateIds = this.cardTypes
          .filter((ct) => [DomainModelRole.AGGREGATE_ROOT, DomainModelRole.COMMAND].includes(ct.domainModelRole))
          .map((ct) => ct.id)

        // check if there is already a card with the same card type in the event
        // that is either a command or aggregate root
        const eventTriggerCard = eventCards.find(
          (card) => triggerAggregateIds.includes(card.cardTypeId) && card.cardTypeId === cardTypeId
        )

        if (eventTriggerCard) {
          this.$store.commit('snackbar/showMessage', {
            content: 'Error adding. Cannot add multiple cards for Aggregate Root or Command card type.',
            timeout: 3000,
            color: 'red',
            location: 'center center'
          }, { root: true })
          return
        }
      }

      EventBus.$emit('open-card-on-event', { eventId: this.node.id, cardTypeId })
      const response = await workflowApi.createCard(this.$route.params.id, eventId, {
        cardTypeId,
        description: text
      })
      if (response.cards?.length) {
        EventBus.$emit('focus-card', { id: response.cards[0].id })
      }
    },

    async addLane (svgGraph, svgLanes, svgGroups, workflow, roleName, selectLane, updateWorkflow = true) {
      const newLaneId = workflowApiHelpers.addLane(svgLanes, roleName)
      if (updateWorkflow) {
        await workflowApi.updateWorkflow({
          svgGraph,
          svgLanes,
          svgGroups,
          workflow,
          updateNote: {
            action: 'added lane',
            target: roleName
          }
        })
      }
      return svgLanes[newLaneId]
    },

    async addLaneRelativeTo (relativeLaneId, direction, customName) {
      const { lanes: originalLanes = [] } = this.workflow
      const relativeLaneIndex = originalLanes.findIndex((lane) => lane.id === relativeLaneId)

      if (relativeLaneIndex === -1) {
        return
      }

      const newLaneInsertionIndex = direction === 'above' ? relativeLaneIndex : relativeLaneIndex + 1
      const roleName = customName || ('Lane ' + ((originalLanes.length || 0) + 1))
      const newLaneId = randomUUID()

      const finalWorkflowLanes = [...originalLanes]
      finalWorkflowLanes.splice(newLaneInsertionIndex, 0, { id: newLaneId, name: roleName })

      const laneHeightMultipliers = {}
      Object.values(this.svgGraph).forEach((node) => {
        laneHeightMultipliers[node.laneId] = Math.max(laneHeightMultipliers[node.laneId] || 1, node.offset + 1)
      })

      const newSvgLanes = {}
      let laneAboveId = null
      finalWorkflowLanes.forEach((lane, index) => {
        const heightMultiplier = laneHeightMultipliers[lane.id] || 1
        const height = 156 * heightMultiplier

        newSvgLanes[lane.id] = {
          id: lane.id,
          x: 0,
          y: laneAboveId ? newSvgLanes[laneAboveId].y + newSvgLanes[laneAboveId].height : 0,
          height,
          name: lane.name,
          laneAbove: laneAboveId,
          emptyLane: !laneHeightMultipliers[lane.id],
          index
        }
        if (laneAboveId) {
          newSvgLanes[laneAboveId].laneBelow = lane.id
        }
        laneAboveId = lane.id
      })

      if (laneAboveId) {
        newSvgLanes[laneAboveId].height += 15
      }

      const newSvgGraph = safeStructuredClone(this.svgGraph)
      Object.values(newSvgGraph).forEach((node) => {
        const parentLane = newSvgLanes[node.laneId]
        if (parentLane) {
          node.y = parentLane.y + 30 + node.offset * 156
        }
      })

      await workflowApi.updateWorkflow({
        svgGraph: newSvgGraph,
        svgLanes: newSvgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'added lane',
          target: roleName
        }
      })

      return newSvgLanes[newLaneId]
    },
    isSelectedEvent (eventId) {
      if (Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 0) {
        return this.selectedEventIds.includes(eventId)
      }
      return eventId === this.$route.params.eventId
    },

    currentEventIsAtTheBottomOfItsLane (svgLanes, svgGraph, eventId) {
      const event = svgGraph[eventId]
      const lane = svgLanes[event?.laneId]
      let bottomOfLane = 0
      if (lane) {
        bottomOfLane = lane.y + lane.height
      }
      return bottomOfLane === (event?.y + 126) || bottomOfLane === (event?.y + 141)
    },

    async blurActionOnRoleName (ev, oldValue, index) {
      const { textContent: newValue, isContentEditable } = ev.target
      if (newValue.length === 0) {
        this.restoreValue(ev, oldValue)
        return
      }
      if (isContentEditable) {
        ev.target._ignoreBlur = true
        ev.target.blur()
        if (this.workflow.lanes[index]) {
          const id = this.workflow.lanes[index].id
          if (newValue === this.workflow.lanes[index].name) {
            return
          }
          this.svgLanes[id].name = newValue
          if (ev.relatedTarget?.classList?.contains('captures-description')) {
            // another button was pushed that will
            // handle the update of the description
            return
          }
          workflowApi.updateWorkflow({
            svgLanes: this.svgLanes,
            svgGraph: this.svgGraph,
            svgGroups: this.svgGroups,
            workflow: this.workflow,
            updateNote: {
              action: 'updated lane name to',
              target: newValue
            }
          })
        }
      }
    },

    async blurOnRoleName (ev, oldValue, index) {
      if (this.isBrowserSafari()) {
        // wait for possible click event to fire and update the svgGraph
        setTimeout(() => { this.blurActionOnRoleName(ev, oldValue, index) }, 1000)
      } else {
        this.blurActionOnRoleName(ev, oldValue, index)
      }
    },

    isBrowserSafari () {
      const browserUserAgent = window.navigator.userAgent.toLowerCase()
      return browserUserAgent &&
        (browserUserAgent.indexOf('safari/') > 0) &&
        !(browserUserAgent.indexOf('edg/') > 0) &&
        !(browserUserAgent.indexOf('chrome/') > 0)
    },

    getCommandCardType (cardTypes) {
      return cardTypes?.find((type) => type.domainModelRole === DomainModelRole.COMMAND)
    },

    getQueryCardType (cardTypes) {
      return cardTypes?.find((type) => type.domainModelRole === DomainModelRole.READ_MODEL)
    },

    getAggregateCardType (cardTypes) {
      return cardTypes?.find((type) => type.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
    },

    getDomainEventCardType (cardTypes) {
      return cardTypes?.find((type) => type.domainModelRole === DomainModelRole.DOMAIN_EVENT)
    },

    rectanglesOverlap (rectA = {}, rectB = {}) {
      if (!rectA || !rectB) {
        return false
      }
      const ax2 = rectA.x + rectA.width
      const ay2 = rectA.y + rectA.height
      const bx2 = rectB.x + rectB.width
      const by2 = rectB.y + rectB.height
      return rectA.x < bx2 && ax2 > rectB.x && rectA.y < by2 && ay2 > rectB.y
    },

    buildSelectionAdjacency (selectedSet, svgGraph) {
      if (!selectedSet || selectedSet.size === 0 || !svgGraph) {
        return new Map()
      }
      const adjacency = new Map()
      selectedSet.forEach(id => {
        const node = svgGraph[id]
        if (!node) {
          return
        }
        const neighbors = new Set()
        if (Array.isArray(node.parents)) {
          for (const parentId of node.parents) {
            if (selectedSet.has(parentId)) {
              neighbors.add(parentId)
            }
          }
        }
        if (Array.isArray(node.childIds)) {
          for (const childId of node.childIds) {
            if (selectedSet.has(childId)) {
              neighbors.add(childId)
            }
          }
        }
        adjacency.set(id, Array.from(neighbors))
      })
      return adjacency
    },

    sortEventsByHierarchy (events) {
      // Create a map of event IDs to events for quick lookup
      const eventMap = new Map()
      events.forEach(event => {
        eventMap.set(event.id, event)
      })

      // Build a set of all event IDs in the selection
      const eventIds = new Set(events.map(e => e.id))

      // Build adjacency list: for each event, track which events it depends on (its parents within the selection)
      const dependencies = new Map()
      events.forEach(event => {
        const deps = []
        if (event.parents) {
          event.parents.forEach(parentId => {
            // Only include parents that are also in the selection
            if (parentId !== 'start' && eventIds.has(parentId)) {
              deps.push(parentId)
            }
          })
        }
        dependencies.set(event.id, deps)
      })

      // Topological sort: events with no dependencies (root events) come first
      const sorted = []
      const visited = new Set()
      const visiting = new Set()

      const visit = (eventId) => {
        if (visiting.has(eventId)) {
          // Circular dependency detected, just add it
          return
        }
        if (visited.has(eventId)) {
          return
        }

        visiting.add(eventId)
        const deps = dependencies.get(eventId) || []
        deps.forEach(depId => {
          if (eventIds.has(depId)) {
            visit(depId)
          }
        })
        visiting.delete(eventId)
        visited.add(eventId)
        sorted.push(eventMap.get(eventId))
      }

      // Visit all events
      events.forEach(event => {
        if (!visited.has(event.id)) {
          visit(event.id)
        }
      })

      return sorted
    },

    /**
     * Validates if multi-selected events can be safely deleted without breaking workflow connections.
     *
     * Rules:
     * - The entire selection must have at most 1 external parent (input connection)
     * - The entire selection must have at most 1 external child (output connection)
     * - External connections are those to events NOT in the selection
     *
     * @param {Array<string>} selectedEventIds - Array of selected event IDs
     * @param {Array<Object>} events - All events in the workflow
     * @param {Object} svgGraph - SVG graph data containing node information
     * @returns {boolean} - True if events can be safely deleted, false otherwise
     */
    canSafelyDeleteMultiSelectedEvents (selectedEventIds, events, svgGraph) {
      // Single selection or empty selection
      if (!Array.isArray(selectedEventIds) || selectedEventIds.length <= 1) {
        return true
      }

      const selectedSet = new Set(selectedEventIds)
      const externalParents = new Set()
      const externalChildren = new Set()

      // Collect all external parents and children from selected events (same logic as getMultiSelectedEventsInputOutput)
      for (const eventId of selectedEventIds) {
        const node = svgGraph[eventId]
        if (!node) {
          continue
        }

        // Collect external parents (input connections)
        if (Array.isArray(node.parents)) {
          node.parents.forEach(parentId => {
            if (parentId !== 'start' && !selectedSet.has(parentId)) {
              externalParents.add(parentId)
            }
          })
        }

        // Collect external children (output connections)
        if (Array.isArray(node.childIds)) {
          node.childIds.forEach(childId => {
            if (!selectedSet.has(childId)) {
              externalChildren.add(childId)
            }
          })
        }
      }

      // The entire selection must have at most 1 external input and at most 1 external output
      if (externalParents.size > 1 || externalChildren.size > 1) {
        return false
      }

      // Fifth check: external parent must not be a fork (must have only 1 output connection)
      for (const parentId of externalParents) {
        const parentNode = svgGraph[parentId]
        if (parentNode && Array.isArray(parentNode.childIds) && parentNode.childIds.length > 1) {
          return false
        }
      }

      // Sixth check: external child must not be a merge (must have only 1 input connection)
      for (const childId of externalChildren) {
        const childNode = svgGraph[childId]
        if (childNode && Array.isArray(childNode.parents) && childNode.parents.length > 1) {
          return false
        }
      }

      return true
    },

    /**
     * Gets the input and output event IDs for multi-selected events.
     *
     * Returns the external parent (input) and external child (output) event IDs
     * that connect to events outside the selection. If there are multiple or no
     * external connections, returns null for that connection.
     *
     * @param {Array<string>} selectedEventIds - Array of selected event IDs
     * @param {Array<Object>} events - All events in the workflow
     * @param {Object} svgGraph - SVG graph data containing node information
     * @returns {Object} - Object with inputId and outputId properties (null if not exactly one)
     */
    getMultiSelectedEventsInputOutput (selectedEventIds, events, svgGraph) {
      if (!Array.isArray(selectedEventIds) || selectedEventIds.length <= 1) {
        return { inputId: null, outputId: null }
      }

      const selectedSet = new Set(selectedEventIds)
      const externalParents = new Set()
      const externalChildren = new Set()

      // Collect all external parents and children from selected events
      for (const eventId of selectedEventIds) {
        const node = svgGraph[eventId]
        if (!node) {
          continue
        }

        // Collect external parents (input connections)
        if (Array.isArray(node.parents)) {
          node.parents.forEach(parentId => {
            if (parentId !== 'start' && !selectedSet.has(parentId)) {
              externalParents.add(parentId)
            }
          })
        }

        // Collect external children (output connections)
        if (Array.isArray(node.childIds)) {
          node.childIds.forEach(childId => {
            if (!selectedSet.has(childId)) {
              externalChildren.add(childId)
            }
          })
        }
      }

      // Return the single input/output ID if exactly one exists, otherwise null
      return {
        inputId: externalParents.size === 1 ? Array.from(externalParents)[0] : null,
        outputId: externalChildren.size === 1 ? Array.from(externalChildren)[0] : null
      }
    }
  }
}
